import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { menuData } from './menuData.js';
import { useAuth } from '../context/AuthContext.jsx';
import { canAccess } from '../config/permissions.js';
import HelpDrawer from '../components/help/HelpDrawer.jsx';

const APP_NAME = 'LogiOS eCom';

// Hide menu entries the current role can't open, so the nav only ever links to
// pages the user can actually reach (no dead 403 clicks). Groups with no visible
// children drop out entirely.
function visibleGroups(role) {
  return menuData.groups
    .map((g) => ({ ...g, children: g.children.filter((c) => canAccess(c.slug, role)) }))
    .filter((g) => g.children.length > 0);
}

// Shared application shell (header / navigation / footer / content outlet) used
// by every web layout. Translated from the raw design layout
// (RawHTMLDesign/layouts/_default-web-layout.html): a fixed-top Bootstrap
// navbar carrying the brand, the slug-routed menu, and the session/account
// chrome, with the page body injected through the router <Outlet />.
export default function AppShell() {
  const [open, setOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const groups = visibleGroups(user?.role);

  const closeNav = () => setOpen(false);

  const handleLogout = () => {
    closeNav();
    logout();
    navigate('/login');
  };

  return (
    <div className="app-layout">
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary sticky-top shadow-sm">
        <div className="container-fluid">
          <Link className="navbar-brand fw-semibold d-flex align-items-center gap-2" to="/" onClick={closeNav}>
            <span className="brand-logo" aria-hidden="true">
              <i className="fa fa-truck-fast"></i>
            </span>
            <span>{APP_NAME}</span>
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            aria-controls="navbarNav"
            aria-expanded={open}
            aria-label="Toggle navigation"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse${open ? ' show' : ''}`} id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {groups.map((group) => (
                <li className="nav-item dropdown" key={group.label}>
                  <a
                    className="nav-link dropdown-toggle text-white"
                    href="#"
                    id={`menu-${group.label.replace(/\W+/g, '_')}`}
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {group.icon && <i className={`${group.icon} me-1`} aria-hidden="true"></i>}
                    {group.label}
                  </a>
                  <ul className="dropdown-menu" aria-labelledby={`menu-${group.label.replace(/\W+/g, '_')}`}>
                    {group.children.map((item) => (
                      <li key={item.slug}>
                        <NavLink
                          className={({ isActive }) =>
                            `dropdown-item${isActive ? ' active' : ''}`
                          }
                          to={`/${item.slug}`}
                          onClick={closeNav}
                        >
                          {item.icon && <i className={`${item.icon} me-2 text-secondary`} aria-hidden="true"></i>}
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>

            {/* Session / account chrome — driven by auth state */}
            <ul className="navbar-nav align-items-lg-center">
              {isAuthenticated && (
                <li className="nav-item d-flex align-items-center me-lg-2 my-2 my-lg-0">
                  <button
                    type="button"
                    className="help-btn"
                    title="Help & Documentation"
                    aria-label="Open help"
                    onClick={() => setShowHelp(true)}
                  >
                    <i className="fas fa-question" aria-hidden="true"></i>
                  </button>
                </li>
              )}
              {isAuthenticated ? (
                <li className="nav-item dropdown">
                  <a
                    className="nav-link dropdown-toggle text-white d-flex align-items-center gap-2"
                    href="#"
                    id="accountMenu"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span
                      className="rounded-circle bg-light text-primary fw-bold d-inline-flex align-items-center justify-content-center"
                      style={{ width: 30, height: 30, fontSize: '0.8rem' }}
                      aria-hidden="true"
                    >
                      {(user?.name || user?.email || 'A').trim().charAt(0).toUpperCase()}
                    </span>
                    <span>{user?.name || user?.email || 'Account'}</span>
                  </a>
                  <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                    <li>
                      <Link className="dropdown-item" to="/account" onClick={closeNav}>
                        <i className="fa fa-user me-2 text-secondary" aria-hidden="true"></i>
                        Profile
                      </Link>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button className="dropdown-item" type="button" onClick={handleLogout}>
                        <i className="fa fa-right-from-bracket me-2 text-secondary" aria-hidden="true"></i>
                        Logout
                      </button>
                    </li>
                  </ul>
                </li>
              ) : (
                <li className="nav-item">
                  <Link className="btn btn-light btn-sm fw-semibold" to="/login" onClick={closeNav}>
                    <i className="fa fa-right-to-bracket me-1" aria-hidden="true"></i>
                    Login
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      <main className="app-content">
        <Outlet />
      </main>

      <footer className="app-footer text-center text-muted py-4 mt-5 border-top">
        <div className="container">
          <small>&copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.</small>
        </div>
      </footer>

      {isAuthenticated && <HelpDrawer isOpen={showHelp} onClose={() => setShowHelp(false)} />}
    </div>
  );
}
